<i18n>
{
  "en": {
    "title": "Section \"{0}\"",
    "groupString": "at {0} group",
    "saveProductPricesError": "Error saving menu engineering. Please, try it later.",
    "saveProductPricesSuccess": "Saved menu engineering successfully",
    "selectLanguage": "Select the language to display"
  },
  "es": {
    "title": "Sección \"{0}\"",
    "groupString": "en agrupación {0}",
    "saveEngineeringError": "Error guardando ingeniería del menú. Inténtelo de nuevo más tarde.",
    "saveEngineeringSuccess": "Se ha guardado la ingeniería del menú correctamente",
    "selectLanguage":  "Seleccionar el lenguaje a mostrar"
  }
}
</i18n>

<template>
  <v-container v-if="currentDemandObject">
    <v-row class="mb-3 align-center">
      <v-col class="d-flex flex-column mt-lg-4 mt-md-4 justify-md-space-between" cols="12" sm="6">
        <div class="text-center font-weight-bold mt-1 mb-2 text-h7"> {{ title }}</div>
        <dynameat-dropdown :itemSelected="selectedService" element-key="guid" element-label="name"
                           :list="serviceList" :disabled-list="disabledServices"
                           @on-select="onSelectService"
                           :use-buttons="true" :class="buttonListClass"/>
      </v-col>

      <v-col class="d-flex mt-lg-4 mt-md-4 justify-md-space-between align-center" cols="12" sm="6">
        <div class="mr-3">
          <menu-engineer-info-button/>
        </div>
        <div>
          <v-select v-model="language"
                      :items="pointOfSaleLanguages"
                      item-text="name"
                      item-value="alternative"
                      :label="$t('selectLanguage')"
                      hide-details
                      height="42"
          />
        </div>
      </v-col>
    </v-row>
    <item-list :itemList="currentItemList" @special="onSpecial" @special-frame="onSpecialFrame"
               @is-visible="onVisible" @move="onMove" @dragged="onDragged"
               v-if="selectedService" :isEditable="isRecommendationEditable"
               class="mt-8"/>
    <dynameat-confirmation-buttons @cancel="onCancel" @confirm="onSave"
                                   :confirm-enabled="isConfirmationAllowed"/>
  </v-container>
</template>

<script>
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import DynameatConfirmationButtons from '@dynameat/components/DynameatConfirmationButtons';
import { routeOutConfirmationMixin } from '@core/mixins';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import {
  Components, MapActions, MapGetters, Mixins
} from '@core/helpers/decorators';
import Vue from 'vue';
import { serviceMixins } from '@pointOfSale/mixins';
import DynameatDropdown from '@dynameat/components/DynameatDropdown';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { sentryLogException } from '@core/helpers/sentryLogger';
import EngineeringProfitabilityChart from '../components/EngineeringProfitabilityChart.vue';
import { SectionRecommendation } from '../models';
import ItemList from '../components/ItemList.vue';
import MenuEngineerInfoButton from '../components/MenuEngineerInfoButton.vue';

@Component
@Components({
  ItemList,
  DynameatConfirmationButtons,
  DynameatDropdown,
  MenuEngineerInfoButton,
  EngineeringProfitabilityChart
})
@MapGetters(['groupList', 'menuRecommendation', 'pointOfSaleLanguages', 'optimizerLanguage'])
@MapActions(['saveMenuEngineeringSection', 'addNotification', 'setEngineeringProfitabilityLoading',
  'sectionProfitabilityRequest', 'sectionByDemandProfitabilityRequest', 'selectPriceLanguage'])
@Mixins([routeOutConfirmationMixin, serviceMixins])
export default class MenuRecommendationDetail extends Vue {
  currentDemandObject = null;

  selectedService = null;

  movingItem = false;

  dragging = false;

  language = 'alt1'

  sectionName = ''

  @Prop(SectionRecommendation) section;

  @Prop(String) groupCodeName;

  @Prop({ type: Boolean, default: false }) isRecommendationEditable;

  get currentItemList() {
    const currentService = this.currentDemandObject.services.find((el) => el.guid === this.selectedService?.guid);
    if (currentService) {
      const subsectionsProducts = [...currentService.products, ...currentService.subsections];
      return subsectionsProducts.sort((a, b) => (a.order > b.order ? 1 : -1));
    }
    return [];
  }

  get title() {
    if (this.groupList.length !== 0) {
      const group = this.groupList.find((el) => el.code_name === this.currentDemandObject.code_name);
      return `${this.$t('title', [this.sectionName])} ${this.$t('groupString', [group.translation.toLowerCase()])}`;
    }
    return '';
  }

  get demandObject() {
    return this.section?.getRecommendationByDemand(this.groupCodeName);
  }

  get hasChanged() {
    return this.currentDemandObject && this.currentDemandObject.hasChange();
  }

  get isConfirmationAllowed() {
    return this.hasChanged && this.isRecommendationEditable;
  }

  get demandServicesGuid() {
    const { services } = this.currentDemandObject;
    if (!services) {
      return [];
    }
    const servicesWithProductsOrSubsections = services.filter((svc) => svc.products.length !== 0 || svc.subsections.length !== 0);
    return servicesWithProductsOrSubsections.map((service) => service.guid);
  }

  get availableServices() {
    return this.serviceList.filter((service) => this.demandServicesGuid.includes(service.guid));
  }

  get disabledServices() {
    return this.serviceList.filter((service) => !this.demandServicesGuid.includes(service.guid));
  }

  get topClasses() {
    return {
      'info-container': this.$vuetify.breakpoint.mdAndUp,
      'price-chart': this.$vuetify.breakpoint.mdAndUp,
      'mobile-chart-container': this.$vuetify.breakpoint.smAndDown
    };
  }

  get buttonListClass() {
    return {
      'buttons-in-front': this.$vuetify.breakpoint.smAndDown
    };
  }

  created() {
    if (!this.section) {
      this.$router.push({
        name: 'menu-recommendation',
        params: { periodGuid: this.$route.params.periodGuid }
      });
    }
    this.currentDemandObject = cloneDeep(this.demandObject);
    if (this.availableServices.length > 0) this.selectedService = this.availableServices[0];
    if (this.section) {
      this.language = this.optimizerLanguage;
      this.handleSectionTitle(this.language);
    }
  }

  @Watch('hasChanged')
  onHasChanged(newHasChanged) {
    this.allowRouteExitWithoutConfirmation = !newHasChanged;
  }

  onSelectService(selectedService) {
    this.selectedService = selectedService;
  }

  getItemByGuid(guid) {
    let found = this.currentItemList.find((el) => el.guid === guid);
    if (!found) {
      this.currentItemList.forEach((el) => {
        found = found || el.products?.find((pr) => pr.guid === guid);
      });
    }
    return found;
  }

  getParentByItemGuid(guid) {
    return this.currentItemList.find((el) => el.products?.find((pr) => pr.guid === guid))?.products || this.currentItemList;
  }

  onSpecial(item) {
    const itemToChange = this.getItemByGuid(item.guid);
    itemToChange.special = !itemToChange.special;
    this.debouncedSectionProfitabilityRequest();
  }

  onSpecialFrame(item) {
    const itemToChange = this.getItemByGuid(item.guid);
    itemToChange.special_frame = !itemToChange.special_frame;
    this.debouncedSectionProfitabilityRequest();
  }

  onVisible(item) {
    const itemToChange = this.getItemByGuid(item.guid);
    itemToChange.is_visible = !itemToChange.is_visible;
    this.debouncedSectionProfitabilityRequest();
  }

  onMove(item, value) {
    if (!this.movingItem) {
      try {
        this.movingItem = true;
        const parentArray = this.getParentByItemGuid(item.guid);
        let itemToSwap = null;
        if (value === 'up') { // move up
          const siblings = parentArray.filter((product) => product.order < item.order);
          itemToSwap = siblings[siblings.length - 1];
        } else if (value === 'down') { // move down
          itemToSwap = parentArray.find((product) => product.order > item.order);
        } else {
          return;
        }
        const oldOrder = item.order;
        item.order = itemToSwap.order;
        itemToSwap.order = oldOrder;
        this.debouncedSectionProfitabilityRequest();
      } finally {
        this.movingItem = false;
      }
    }
  }

  debouncedSectionProfitabilityRequest = debounce(this.reloadProfitabilitySectionRequest, 1500);

  onDragged({ item, distance, direction }) {
    if (!this.dragging) {
      try {
        this.dragging = true;
        for (let i = 0; i < distance; i++) {
          this.onMove(item, direction);
        }
      } finally {
        this.dragging = false;
      }
    }
  }

  async reloadProfitabilitySectionRequest() {
    // If there are section changes to commit
    const { periodGuid } = this.$route.params;
    const menuGuid = this.menuRecommendation.guid;
    try {
      await this.setEngineeringProfitabilityLoading(1);
      // Clone section, remove that demand changes and apply new changes by demand, mantaining all changes of other demand types
      const sectionModified = cloneDeep(this.section);
      sectionModified.recommendations_by_demand = sectionModified.recommendations_by_demand.filter((r) => r.code_name !== this.groupCodeName);
      sectionModified.recommendations_by_demand.push(this.currentDemandObject);
      await this.sectionByDemandProfitabilityRequest({ periodGuid, menuGuid, sectionModified });
    } catch (err) {
      sentryLogException(err, `Error get getting new profitability for section ${this.section.guid} in period ${periodGuid}`);
    } finally {
      await this.setEngineeringProfitabilityLoading(-1);
    }
  }

  async onSave() {
    try {
      await this.saveMenuEngineeringSection({
        sectionGuid: this.section.guid,
        demandObject: this.currentDemandObject
      });
      this.allowRouteExitWithoutConfirmation = true;
      this.$router.replace({ name: 'menu-recommendation' });
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('saveEngineeringError')));
    }
  }

  onCancel() {
    this.$router.replace({ name: 'menu-recommendation' }, this.resetProfitability);
  }

  async resetProfitability() {
    const { params: { periodGuid } } = this.$route;
    const menuGuid = this.menuRecommendation.guid;
    try {
      await this.setEngineeringProfitabilityLoading(1);
      if (this.isConfirmationAllowed) {
        await this.sectionProfitabilityRequest({
          periodGuid,
          menuGuid,
          sectionGuids: [this.section.guid]
        });
      }
    } catch (err) {
      sentryLogException(err, `Error cancelling section changes and getting new profitability for section ${this.section.guid}`);
    } finally {
      await this.setEngineeringProfitabilityLoading(-1);
    }
  }

  handleSectionTitle(language) {
    const newText = this.section[`name${language.charAt(0).toUpperCase() + language.slice(1)}`];
    if (newText !== '') {
      this.sectionName = newText;
    }
  }

  @Watch('language', { immediate: false })
  onLanguageChanged(newLanguage, oldLanguage) {
    if (newLanguage !== oldLanguage) {
      this.selectPriceLanguage(newLanguage);
      this.handleSectionTitle(newLanguage);
    }
  }
}
</script>

<style scoped lang="css">
.info-container {
  margin-bottom: -70px;
}

.price-chart {
  margin-top: -50px;
  margin-bottom: -30px;
}

.mobile-chart-container {
  margin-top: 30px;
  margin-bottom: -30px;
}

.buttons-in-front {
  z-index: 1
}
</style>
